import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { ColDef, ICellRendererParams, SelectionChangedEvent, CellValueChangedEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { HStack, Modal, VStack } from '@musinsa/mwms-ds'
import { PrimaryButton, SecondaryButton, NegativeButton, Typography, TextButton, Textfield, Tab } from '@musinsa/layer-components-desktop'
import { Icon } from '@musinsa/pbo-assets'

import { PageLayout } from '@/components/layout/PageLayout'
import TitleSection from '@/components/content/TitleSection'
import FilterListV2, { TFilterItem } from '@/components/content/FilterListV2'
import FloatingMultiSelect from '@/components/content/FloatingMultiSelect'
import { FloatingLabelWrapper } from '@/components/content/FloatingLabelWrapper'
import ResizableContentsV2 from '@/components/content/ResizableContentsV2'
import GridTableV2 from '@/components/tableV2/GridTableV2'
import { Pagination, SearchBar } from '@/components/tableV2/GridTableV2Toolbar'
import { usePagination } from '@/hooks/usePagination'
import { SnackBarListBox, SnackBarItemData, SnackBarVariant } from '@/components/content/SnackBar'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import MAlert from '@/utils/modal/MAlert'
import { MasterUser, ShipperMasterRow, USER_MANAGEMENT_TABS, UserManagementTab } from '@/services/master/userManagement/types'
import { checkPhoneDuplicate, PAGE_SIZE_ON_USER_MANAGEMENT } from '@/services/master/userManagement/apis'
import {
  useActivateUsers,
  useApproveUser,
  useDeactivateUsers,
  useDeleteUsers,
  useGetMasterAuthCodes,
  useGetMasterShippers,
  useGetMasterShipperOptions,
  useGetMasterUsers,
  useGetMasterUserTabCounts,
  useResendInvites,
  useResetMfa,
  useResetPasswords,
  useUpdateUserProfile,
  useUpdateShipperMultiGroup,
  useSetMasterShipperUseFlag,
  useAddMasterDepartment,
} from '@/services/master/userManagement/useUserManagementQueries'
import { formatDateTime, SNACK_SUCCESS, EDITABLE_TEXT_CLASS, EDITABLE_SELECT_CLASS, AG_SELECT_EDITOR, PHONE_REGEX } from './constants'
import { OwnerRadioCellRenderer } from './components/cells'
import { FigmaDialog } from './components/FigmaDialog'
import {
  ActionBar,
  AgEditableCellStyles,
  BOLD_CELL,
  DialogBody,
  DialogFooter,
  DisabledInput,
  FieldGroup,
  FieldLabelRow,
  FormSection,
  GroupDivider,
  GroupTag,
  GroupTagRemoveBtn,
  GroupTagWrap,
  MONO_CELL,
  MONO_MUTED_CELL,
  TabWrapper,
  UserTableWrap,
} from './components/styles'

/* ─── Types ───────────────────────────────────────────────────────────────── */

interface OwnerFilterForm {
  shipperCodes?: string[] // 화주 멀티선택 — 화주명 표시·화주코드 전송(화주명 비유니크)
  oktaUserId?: string // 화주 무관 유저 아이디 검색
}

/** 탭/화주 선택을 URL 쿼리로 구분 — 새로고침·딥링크에도 동일 화면 보장 (gi23 tabType 패턴) */
interface UserManagementUrlParams {
  tab?: UserManagementTab
  shipperCode?: string
}

let _snackId = 0
function nextSnackId() {
  return String(++_snackId)
}

// 스낵 리스트 변환 헬퍼 — 인라인 중첩 콜백을 모듈 레벨로 분리(중첩 깊이 완화)
const markSnackClosing = (id: string) => (list: SnackBarItemData[]) => list.map((s) => (s.id === id ? { ...s, closing: true } : s))
const removeSnackById = (id: string) => (list: SnackBarItemData[]) => list.filter((s) => s.id !== id)

// boolean → 'Y'/'N' (값 없으면 빈 문자열). 중첩 삼항 방지용 헬퍼.
function booleanToYn(value?: boolean | null): string {
  if (value == null) return ''
  return value ? 'Y' : 'N'
}

// 멀티화주그룹 요약 라벨 (없음 '-', 1개 그대로, 그 외 'A 외 N개')
function multiGroupLabel(groups: string[]): string {
  if (groups.length === 0) return '-'
  if (groups.length === 1) return groups[0]
  return `${groups[0]} 외 ${groups.length - 1}개`
}

// 유저 그리드 빈 목록 오버레이 메시지 (중첩 삼항 방지)
function userGridEmptyMessage(hasKeyword: boolean, hasShipper: boolean): string {
  if (hasKeyword) return '검색결과가 없습니다.'
  if (hasShipper) return '표시할 사용자가 없습니다.'
  return '화주를 선택하면 사용자 목록이 표시됩니다.'
}

function OktaIdFilterInput({ isOnPanel, onSearch }: { isOnPanel?: boolean; onSearch: () => void }) {
  const { watch, setValue } = useFormContext<OwnerFilterForm>()
  const value = watch('oktaUserId') ?? ''
  const input = (
    <Textfield.Box style={{ height: 44, width: isOnPanel ? '100%' : 220 }}>
      <Textfield.Input
        value={value}
        onValueChange={(v: string) => setValue('oktaUserId', v || undefined)}
        placeholder="Okta 유저 ID 입력"
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault()
            onSearch()
          }
        }}
      />
    </Textfield.Box>
  )
  return isOnPanel ? input : <FloatingLabelWrapper label="Okta 유저 ID">{input}</FloatingLabelWrapper>
}

/* ─── Main component ────────────────────────────────────────────────────────── */

function UserManagement() {
  const ownerGridRef = useRef<AgGridReact>(null)
  const userGridRef = useRef<AgGridReact>(null)

  // ── URL params (탭 / 선택 화주) ──
  const { parsedQs, replaceQuery } = useParsedQuery<UserManagementUrlParams>()
  const tab: UserManagementTab = USER_MANAGEMENT_TABS.includes(parsedQs.tab as UserManagementTab) ? (parsedQs.tab as UserManagementTab) : 'active'
  const selectedShipperCode = parsedQs.shipperCode

  // ── Owner filter form ──
  const ownerForm = useForm<OwnerFilterForm>({ defaultValues: { shipperCodes: [], oktaUserId: undefined } })
  const { watch: ownerWatch, setValue: ownerSetValue } = ownerForm

  // ── 검색 적용 상태 (검색 버튼으로 확정) ── 진입 시 빈 화면, 검색해야 조회
  const [hasSearched, setHasSearched] = useState(false)
  const [searchedShipperCodes, setSearchedShipperCodes] = useState<string[]>([])
  const [searchedEmail, setSearchedEmail] = useState<string>('')

  // 라디오로 지정한 화주 — 부서 추가 대상 (하단 목록 필터와 별개)
  const [radioSelectedOwner, setRadioSelectedOwner] = useState<ShipperMasterRow | undefined>(undefined)

  // ── User tab state ──
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set())
  const [userSearch, setUserSearch] = useState('')
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('')

  // ── SnackBar state ──
  const [snackList, setSnackList] = useState<SnackBarItemData[]>([])

  const showSnack = useCallback((message: string, variant: SnackBarVariant) => {
    const id = nextSnackId()
    setSnackList((prev) => [...prev, { id, message, variant }])
    setTimeout(() => {
      setSnackList(markSnackClosing(id))
      setTimeout(() => setSnackList(removeSnackById(id)), 400)
    }, 3000)
  }, [])

  const closeSnack = useCallback((id: string) => {
    setSnackList(markSnackClosing(id))
    setTimeout(() => setSnackList(removeSnackById(id)), 400)
  }, [])

  // 벌크 액션 부분성공 안내 — 전부 성공이면 okMsg, 일부 실패면 성공/실패 건수 표시 (activate/delete 등 success 형태 무관)
  const bulkResultSnack = useCallback(
    (res: { data: { success: unknown[]; fail: unknown[] } }, okMsg: string) => {
      const failN = res.data.fail.length
      if (failN === 0) showSnack(okMsg, SNACK_SUCCESS)
      else showSnack(`${res.data.success.length}건 성공 / ${failN}건 실패`, 'critical')
    },
    [showSnack],
  )

  // ── Pagination ──
  const [ownerPage, setOwnerPage] = useState(0)
  const [ownerPageSize, setOwnerPageSize] = useState(PAGE_SIZE_ON_USER_MANAGEMENT)
  const [userPage, setUserPage] = useState(0)
  const [userPageSize, setUserPageSize] = useState(PAGE_SIZE_ON_USER_MANAGEMENT)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedUserSearch(userSearch)
      setUserPage(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [userSearch])

  // 새로고침/재진입 시 URL 에 남은 이전 조회 상태(화주·탭)를 초기화한다.
  // 상단 검색 폼은 영속되지 않아 진입 시 빈 화면인데, URL 의 shipperCode 로 하단 조회 결과만
  // 복원되면 상하단이 불일치하므로 하단도 빈 화면으로 통일한다. (마운트 1회)
  useEffect(() => {
    if (parsedQs.shipperCode !== undefined || parsedQs.tab !== undefined) {
      replaceQuery({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Queries ──
  // 화주 드롭다운 옵션 (화주사관리 등록 화주 전체 — value:코드, label:화주명)
  const shipperOptionsQuery = useGetMasterShipperOptions()
  // 상단 화주 조회 — 진입 시 빈 화면, 검색 시 조회. 화주코드 다중 또는 이메일(Okta ID) 필터(해당 유저가 속한 화주 반환).
  const shippersQuery = useGetMasterShippers(
    { shipperCodes: searchedShipperCodes, email: searchedEmail || undefined, page: ownerPage, size: ownerPageSize },
    hasSearched,
  )
  const authCodesQuery = useGetMasterAuthCodes(selectedShipperCode)
  // 유저 목록/카운트 — 화주 더블클릭 시에만 조회 (이메일 검색은 상단 화주만 노출, 하단은 더블클릭으로 진입)
  const userQueryEnabled = !!selectedShipperCode
  const tabCountsQuery = useGetMasterUserTabCounts({ shipperCode: selectedShipperCode, email: searchedEmail || undefined }, userQueryEnabled)
  const usersQuery = useGetMasterUsers(
    {
      tab,
      shipperCode: selectedShipperCode,
      email: searchedEmail || undefined,
      keyword: debouncedUserSearch || undefined,
      page: userPage,
      size: userPageSize,
    },
    userQueryEnabled,
  )

  const owners = useMemo(() => shippersQuery.data?.data.items ?? [], [shippersQuery.data])
  const ownerTotalCount = shippersQuery.data?.data.pageInfo.totalCount ?? 0
  const tabUsers = usersQuery.data?.data.items ?? []
  const userTotalCount = usersQuery.data?.data.pageInfo.totalCount ?? 0
  const tabCounts = tabCountsQuery.data?.data

  // ── Mutations ──
  const approveUser = useApproveUser()
  const resendInvites = useResendInvites()
  const resetPasswords = useResetPasswords()
  const resetMfa = useResetMfa()
  const activateUsers = useActivateUsers()
  const deactivateUsers = useDeactivateUsers()
  const deleteUsers = useDeleteUsers()
  const updateUserProfile = useUpdateUserProfile()
  const updateShipperMultiGroup = useUpdateShipperMultiGroup()
  const setMasterShipperUseFlag = useSetMasterShipperUseFlag()
  const addMasterDepartment = useAddMasterDepartment()

  // ── Multi-group modal (화주 멀티화주그룹 — 저장 API spec 미정, BE 협의 전 로컬 반영) ──
  const [ownerGroupsMap, setOwnerGroupsMap] = useState<Map<string, string[]>>(new Map())
  const [multiGroupOwner, setMultiGroupOwner] = useState<string | null>(null)
  const [newGroupInput, setNewGroupInput] = useState('')

  useEffect(() => {
    setOwnerGroupsMap(new Map(owners.map((o) => [o.shipperCode, [...o.multiShipperGroup]])))
  }, [owners])

  // ── Dept dialog (부서 추가 — API spec 미정, BE 협의 필요) ──
  const [deptDialogOpen, setDeptDialogOpen] = useState(false)
  const [deptName, setDeptName] = useState('')
  const [deptManager, setDeptManager] = useState('')

  // ── Action dialogs ──
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [pwResetOpen, setPwResetOpen] = useState(false)
  const [mfaResetOpen, setMfaResetOpen] = useState(false)
  const [approveOpen, setApproveOpen] = useState(false)
  const [inviteResendOpen, setInviteResendOpen] = useState(false)
  const [activationOpen, setActivationOpen] = useState(false)

  // ── Derived ──
  const selectedUserRows = tabUsers.filter((u) => selectedUserIds.has(u.id))
  const selectedUserEmails = selectedUserRows.map((u) => u.email)
  const selectedIds = selectedUserRows.map((u) => u.id)

  // ── Pagination props ──
  const ownerPaginationProps = usePagination({
    totalCount: ownerTotalCount,
    currentPage: ownerPage,
    pageSize: ownerPageSize,
    onChangePage: setOwnerPage,
    onPageSizeChange: setOwnerPageSize,
  })

  const userPaginationProps = usePagination({
    totalCount: userTotalCount,
    currentPage: userPage,
    pageSize: userPageSize,
    onChangePage: setUserPage,
    onPageSizeChange: setUserPageSize,
  })

  // ── Event handlers ──
  const clearUserSelection = useCallback(() => {
    setSelectedUserIds(new Set())
    userGridRef.current?.api?.deselectAll()
  }, [])

  const handleOwnerFilterSearch = useCallback(() => {
    setSearchedShipperCodes(ownerWatch('shipperCodes') ?? [])
    setSearchedEmail((ownerWatch('oktaUserId') ?? '').trim())
    setHasSearched(true)
    ownerGridRef.current?.api?.deselectAll()
    setOwnerPage(0)
    setUserPage(0)
    replaceQuery({ ...parsedQs, shipperCode: undefined })
    clearUserSelection()
  }, [ownerWatch, parsedQs, replaceQuery, clearUserSelection])

  const handleOwnerFilterReset = useCallback(() => {
    ownerSetValue('shipperCodes', [])
    ownerSetValue('oktaUserId', undefined)
    setSearchedShipperCodes([])
    setSearchedEmail('')
    setHasSearched(false)
    ownerGridRef.current?.api?.deselectAll()
    setOwnerPage(0)
    replaceQuery({ ...parsedQs, shipperCode: undefined })
    clearUserSelection()
  }, [ownerSetValue, parsedQs, replaceQuery, clearUserSelection])

  // 라디오 선택 = 부서 추가 대상 지정 (하단 목록과 무관)
  const handleOwnerSelectionChanged = useCallback((e: SelectionChangedEvent<ShipperMasterRow>) => {
    setRadioSelectedOwner(e.api.getSelectedRows()[0])
  }, [])

  // 상단 화주 그리드 사용여부(Y/N) 인라인 저장 (LOGISPRD-9330)
  const handleOwnerCellValueChanged = useCallback(
    (e: CellValueChangedEvent<ShipperMasterRow>) => {
      if (e.colDef.field !== 'isActive' || !e.data) return
      setMasterShipperUseFlag.mutate(
        { shipperCode: e.data.shipperCode, use: e.newValue === 'Y' },
        { onSuccess: () => showSnack('저장 완료되었습니다.', SNACK_SUCCESS) },
      )
    },
    [setMasterShipperUseFlag, showSnack],
  )

  // 행 더블클릭 = 하단 유저 목록 조회 (gi24 상단→하단 연동과 동일 패턴)
  // 진입 시 가입완료 탭으로 고정 — 직전 화주에서 보던 탭이 따라오지 않게 첫 탭부터 시작
  const handleOwnerRowDoubleClicked = useCallback(
    (owner: ShipperMasterRow) => {
      replaceQuery({ ...parsedQs, shipperCode: owner.shipperCode, tab: 'active' })
      clearUserSelection()
      setUserPage(0)
    },
    [parsedQs, replaceQuery, clearUserSelection],
  )

  // 딥링크/새로고침 — URL 의 shipperCode 를 상단 그리드 라디오 선택으로 복원
  const handleOwnerDataRendered = useCallback(() => {
    if (!selectedShipperCode) return
    ownerGridRef.current?.api?.forEachNode((node) => {
      if (node.data?.shipperCode === selectedShipperCode) node.setSelected(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUserSelectionChanged = useCallback((e: SelectionChangedEvent<MasterUser>) => {
    setSelectedUserIds(new Set(e.api.getSelectedRows().map((r) => r.id)))
  }, [])

  /** 유저 그리드 인라인 편집 → 변경 즉시 저장. 휴대폰은 중복 검증 선행. */
  const handleUserCellValueChanged = useCallback(
    (e: CellValueChangedEvent<MasterUser>) => {
      const field = e.colDef.field
      const user = e.data
      if (!field || !user) return

      // select 컬럼 — 권한 / 멀티화주여부 / 유저활성화
      if (field === 'authCode') {
        updateUserProfile.mutate({ id: user.id, body: { authCode: e.newValue } }, { onSuccess: () => showSnack('저장 완료되었습니다.', SNACK_SUCCESS) })
        return
      }
      if (field === 'multiOwner') {
        updateUserProfile.mutate(
          { id: user.id, body: { multiOwner: e.newValue === 'Y' } },
          { onSuccess: () => showSnack('저장 완료되었습니다.', SNACK_SUCCESS) },
        )
        return
      }
      if (field === 'isActive') {
        const toActive = e.newValue === 'Y'
        const mutation = toActive ? activateUsers : deactivateUsers
        mutation.mutate([user.id], { onSuccess: (res) => bulkResultSnack(res, '저장 완료되었습니다.') })
        return
      }

      // 텍스트(text) 컬럼 — 휴대폰은 중복 검증 후 저장
      if (field === 'phone') {
        const newPhone = String(e.newValue ?? '').trim()
        const revert = () => {
          e.node.setDataValue('phone', e.oldValue)
        }
        if (!PHONE_REGEX.test(newPhone)) {
          MAlert.show('유효한 휴대폰번호를 입력하세요.')
          revert()
          return
        }
        checkPhoneDuplicate({ phone: newPhone, excludeUserId: user.id })
          .then((res) => {
            if (res.data.duplicated) {
              MAlert.show('이미 사용 중인 휴대폰번호입니다.')
              revert()
              return
            }
            updateUserProfile.mutate(
              { id: user.id, body: { phone: newPhone } },
              { onSuccess: () => showSnack('저장 완료되었습니다.', SNACK_SUCCESS) },
            )
          })
          .catch(() => revert())
        return
      }
      if (field === 'nickname') {
        const newName = String(e.newValue ?? '').trim()
        if (!newName) {
          MAlert.show('이름을 입력해주세요.')
          e.node.setDataValue('nickname', e.oldValue)
          return
        }
        updateUserProfile.mutate(
          { id: user.id, body: { nickname: newName } },
          { onSuccess: () => showSnack('저장 완료되었습니다.', SNACK_SUCCESS) },
        )
      }
    },
    [activateUsers, deactivateUsers, updateUserProfile, showSnack, bulkResultSnack],
  )

  const switchTab = useCallback(
    (t: UserManagementTab) => {
      replaceQuery({ ...parsedQs, tab: t })
      clearUserSelection()
      setUserPage(0)
    },
    [parsedQs, replaceQuery, clearUserSelection],
  )

  // ── Confirmations (LOGISPRD-8889 / 8532 액션 endpoint 호출) ──
  const confirmDelete = useCallback(() => {
    deleteUsers.mutate(selectedIds, {
      onSuccess: (res) => {
        bulkResultSnack(res, `${selectedIds.length}개 계정이 삭제되었습니다.`)
        setDeleteOpen(false)
        clearUserSelection()
      },
    })
  }, [deleteUsers, selectedIds, bulkResultSnack, clearUserSelection])

  const confirmPwReset = useCallback(() => {
    resetPasswords.mutate(selectedIds, {
      onSuccess: (res) => {
        bulkResultSnack(res, `사용자 ${selectedIds.length}명의 비밀번호 초기화 메일을 발송했습니다.`)
        setPwResetOpen(false)
        clearUserSelection()
      },
    })
  }, [resetPasswords, selectedIds, bulkResultSnack, clearUserSelection])

  const confirmMfaReset = useCallback(() => {
    resetMfa.mutate(selectedIds, {
      onSuccess: (res) => {
        bulkResultSnack(res, `사용자 ${selectedIds.length}명의 MFA를 초기화했습니다.`)
        setMfaResetOpen(false)
        clearUserSelection()
      },
    })
  }, [resetMfa, selectedIds, bulkResultSnack, clearUserSelection])

  const confirmApprove = useCallback(() => {
    // 승인 시 권한 부여 — pending 탭 그리드에서 인라인 편집된 권한 라벨을 함께 전송 (라벨↔AUTH_CD 매핑 BE 협의)
    Promise.all(selectedUserRows.map((user) => approveUser.mutateAsync({ id: user.id, body: { authCode: user.authCode } }))).then(() => {
      showSnack(`${selectedUserRows.length}개의 계정이 가입승인되었습니다.`, SNACK_SUCCESS)
      setApproveOpen(false)
      clearUserSelection()
    })
  }, [approveUser, selectedUserRows, showSnack, clearUserSelection])

  const confirmApproveReject = useCallback(() => {
    // 가입 반려 BE 미구현(PM 결정) — BE 요청 없이 다이얼로그만 닫는다
    setApproveOpen(false)
    clearUserSelection()
  }, [clearUserSelection])

  const confirmInviteResend = useCallback(() => {
    resendInvites.mutate(selectedIds, {
      onSuccess: (res) => {
        bulkResultSnack(res, '초대 메일이 재발송되었습니다.')
        setInviteResendOpen(false)
        clearUserSelection()
      },
    })
  }, [resendInvites, selectedIds, bulkResultSnack, clearUserSelection])

  const confirmActivation = useCallback(() => {
    activateUsers.mutate(selectedIds, {
      onSuccess: (res) => {
        bulkResultSnack(res, `${selectedIds.length}개의 계정 활성화 요청이 승인되었습니다.`)
        setActivationOpen(false)
        clearUserSelection()
      },
    })
  }, [activateUsers, selectedIds, bulkResultSnack, clearUserSelection])

  const handleAddDept = useCallback(() => {
    const shipperCode = radioSelectedOwner?.shipperCode
    if (!shipperCode || !deptName.trim()) return
    addMasterDepartment.mutate(
      { shipperCode, departmentName: deptName.trim(), manager: deptManager.trim() },
      {
        onSuccess: () => {
          showSnack('부서가 추가되었습니다.', SNACK_SUCCESS)
          setDeptDialogOpen(false)
          setDeptName('')
          setDeptManager('')
        },
      },
    )
  }, [radioSelectedOwner, deptName, deptManager, addMasterDepartment, showSnack])

  // ── Multi-group (저장 API spec 미정 — BE 협의 전 로컬 반영) ──
  const openMultiGroup = useCallback((code: string) => {
    setMultiGroupOwner(code)
    setNewGroupInput('')
  }, [])
  const closeMultiGroup = useCallback(() => {
    setMultiGroupOwner(null)
    setNewGroupInput('')
  }, [])
  const removeFromGroup = useCallback((code: string | null, g: string) => {
    if (!code) return
    setOwnerGroupsMap((m) => {
      const n = new Map(m)
      n.set(
        code,
        (m.get(code) ?? []).filter((x) => x !== g),
      )
      return n
    })
  }, [])
  const addToGroup = useCallback(
    (code: string | null) => {
      const val = newGroupInput.trim()
      if (!val || !code) return
      setOwnerGroupsMap((m) => {
        const n = new Map(m)
        const cur = m.get(code) ?? []
        if (!cur.includes(val)) n.set(code, [...cur, val])
        return n
      })
      setNewGroupInput('')
    },
    [newGroupInput],
  )
  const saveMultiGroup = useCallback(() => {
    if (!multiGroupOwner) return
    updateShipperMultiGroup.mutate(
      { shipperCode: multiGroupOwner, body: { shipperCodes: ownerGroupsMap.get(multiGroupOwner) ?? [] } },
      {
        onSuccess: () => {
          showSnack('멀티화주그룹이 저장되었습니다.', SNACK_SUCCESS)
          closeMultiGroup()
        },
      },
    )
  }, [multiGroupOwner, ownerGroupsMap, updateShipperMultiGroup, closeMultiGroup, showSnack])

  // 화주 드롭다운 옵션 (value:화주코드, label:화주명) — 화주명 비유니크라 코드로 식별
  const shipperCodeOptions = shipperOptionsQuery.data ?? []

  // 필터 — 전체 필터(FilterListV2) + 화주 멀티선택(화주명 표시·검색 내장) + Okta 유저 ID(화주 무관)
  const selectedShipperCodes = ownerWatch('shipperCodes') ?? []
  const ownerFilterList = useMemo<TFilterItem<OwnerFilterForm>[]>(
    () => [
      {
        field: 'shipperCodes',
        label: '화주명',
        fieldWidth: 220,
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <FloatingMultiSelect
            floatingLabel={isOnPanel ? undefined : '화주명'}
            options={shipperCodeOptions}
            value={selectedShipperCodes}
            onChange={(v) => ownerSetValue('shipperCodes', v)}
            placeholder="화주명 선택"
            width={isOnPanel ? undefined : 220}
            onEnter={handleOwnerFilterSearch}
          />
        ),
      },
      {
        field: 'oktaUserId',
        label: 'Okta 유저 ID',
        fieldWidth: 220,
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => <OktaIdFilterInput isOnPanel={isOnPanel} onSearch={handleOwnerFilterSearch} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shipperCodeOptions, selectedShipperCodes, handleOwnerFilterSearch],
  )

  // ── Owner column defs (라디오 컬럼 포함 — 인라인 편집 컬럼의 저장 API 는 BE 협의 필요) ──
  const ownerColDefs = useMemo<ColDef<ShipperMasterRow>[]>(
    () => [
      {
        colId: 'radio',
        headerName: '',
        width: 40,
        pinned: 'left' as const,
        lockPosition: 'left' as const,
        lockPinned: true,
        resizable: false,
        sortable: false,
        suppressNavigable: true,
        cellRenderer: OwnerRadioCellRenderer,
        headerClass: 'moms-checkbox-col-header',
        cellClass: 'moms-radio-col-cell',
      },
      // 화주사관리(ma14) 등록 정보 — 조회 전용
      { field: 'shipperCode', headerName: '화주코드', width: 100, cellStyle: MONO_MUTED_CELL },
      { field: 'shipperName', headerName: '화주명', width: 140, cellStyle: BOLD_CELL },
      { field: 'businessNumber', headerName: '사업자번호', width: 130, cellStyle: MONO_CELL },
      { field: 'ceoName', headerName: '대표자명', width: 100 },
      { field: 'companyName', headerName: '상호명', width: 180 },
      { field: 'zipCode', headerName: '우편번호', width: 100, cellStyle: MONO_CELL },
      { field: 'address', headerName: '주소', width: 240 },
      { field: 'phone', headerName: '전화번호', width: 130 },
      {
        field: 'deptNames',
        headerName: '부서명',
        minWidth: 160,
        valueFormatter: ({ value }) => (Array.isArray(value) ? value.join(', ') || '-' : '-'),
      },
      // 유저관리에서 직접 수정 가능 — 사용여부 / 멀티화주그룹
      {
        field: 'isActive',
        headerName: '사용여부',
        width: 96,
        editable: true,
        cellEditor: AG_SELECT_EDITOR,
        cellEditorParams: { values: ['Y', 'N'] },
        valueGetter: (p) => booleanToYn(p.data?.isActive),
        valueSetter: (p) => {
          if (!p.data) return false
          p.data.isActive = p.newValue === 'Y'
          return true
        },
        cellClass: EDITABLE_SELECT_CLASS,
      },
      {
        field: 'multiShipperGroup',
        headerName: '멀티화주그룹',
        width: 160,
        cellDataType: false, // 배열 값 — ag-grid 데이터타입 추론 끔(object 추론 경고 방지)
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: ({ data }: ICellRendererParams<ShipperMasterRow>) => {
          if (!data) return null
          const groups = ownerGroupsMap.get(data.shipperCode) ?? []
          const label = multiGroupLabel(groups)
          return (
            <TextButton
              size="small"
              isUnderline
              label={label}
              onClick={(e) => {
                e.stopPropagation()
                openMultiGroup(data.shipperCode)
              }}
            />
          )
        },
      },
      { field: 'createdBy', headerName: '생성자', width: 90 },
      { field: 'createdAt', headerName: '생성일자', width: 170, cellStyle: MONO_CELL, valueFormatter: ({ value }) => formatDateTime(value) },
      { field: 'updatedBy', headerName: '수정자', width: 90 },
      { field: 'updatedAt', headerName: '수정일자', width: 170, cellStyle: MONO_CELL, valueFormatter: ({ value }) => formatDateTime(value) },
    ],
    [ownerGroupsMap, openMultiGroup],
  )

  // ── Per-tab user column defs ──
  // 권한 옵션 — 선택 화주의 권한코드 목록 (/api/v1/auth/codes). 하단 그리드는 선택 화주 기준이라 전 탭 공통.
  // colDefs 가 authOptions 로드마다 바뀌면 grid 가 재적용되며 행이 사라지므로, 옵션은 ref 로 읽어 colDefs 를 stable 하게 유지.
  // 표시/전송 모두 권한코드(authCode) — PM 요구사항.
  const authOptions = useMemo(() => authCodesQuery.data ?? [], [authCodesQuery.data])
  const authCodesRef = useRef<string[]>([])
  useEffect(() => {
    authCodesRef.current = authOptions.map((a) => a.authCode)
  }, [authOptions])

  // shipperCode → 화주명 매핑 — 유저 목록 API 는 shipperCode 만 주므로 소속(화주) 표시는 화주 옵션에서 resolve. colDefs stable 위해 ref.
  const shipperNameByCodeRef = useRef<Map<string, string>>(new Map())
  useEffect(() => {
    shipperNameByCodeRef.current = new Map((shipperOptionsQuery.data ?? []).map((o) => [o.value, o.label]))
  }, [shipperOptionsQuery.data])

  // 하단 유저 그리드 — 전 탭 공통 컬럼 (그리드 조회 컬럼 스펙). No(행번호)는 useColumnNo 로 자동 추가, 소속(화주)는 상단 그리드 종속.
  const currentUserColDefs = useMemo<ColDef<MasterUser>[]>(
    () => [
      {
        field: 'shipperName',
        headerName: '소속(화주)',
        width: 140,
        // 화주사명(shipperName) 노출 — BE 가 shipperName 미제공 시 shipperCode→화주명 매핑으로 resolve
        valueGetter: (p) => {
          const code = p.data?.shipperCode
          return p.data?.shipperName || (code ? shipperNameByCodeRef.current.get(code) ?? code : '')
        },
      },
      { field: 'departmentName', headerName: '부서', width: 120 },
      { field: 'departmentManager', headerName: '책임자명', width: 104 },
      { field: 'email', headerName: 'Okta 유저 아이디', width: 200, cellStyle: MONO_CELL },
      { field: 'nickname', headerName: '유저 이름', width: 110, editable: true, cellStyle: BOLD_CELL, cellClass: EDITABLE_TEXT_CLASS },
      { field: 'phone', headerName: '휴대폰 번호', width: 140, editable: true, cellClass: EDITABLE_TEXT_CLASS },
      {
        field: 'isActive',
        headerName: '유저 활성화',
        width: 110,
        editable: true,
        cellEditor: AG_SELECT_EDITOR,
        cellEditorParams: { values: ['Y', 'N'] },
        valueGetter: (p) => booleanToYn(p.data?.isActive),
        valueSetter: (p) => {
          if (!p.data) return false
          p.data.isActive = p.newValue === 'Y'
          return true
        },
        cellClass: EDITABLE_SELECT_CLASS,
      },
      {
        field: 'authCode',
        headerName: '권한',
        width: 120,
        editable: true,
        cellEditor: AG_SELECT_EDITOR,
        cellEditorParams: () => ({ values: authCodesRef.current }),
        cellClass: EDITABLE_SELECT_CLASS,
      },
      {
        field: 'multiOwner',
        headerName: '멀티화주여부',
        width: 110,
        editable: true,
        cellEditor: AG_SELECT_EDITOR,
        cellEditorParams: { values: ['Y', 'N'] },
        valueGetter: (p) => (p.data?.multiOwner ? 'Y' : 'N'),
        valueSetter: (p) => {
          if (!p.data) return false
          p.data.multiOwner = p.newValue === 'Y'
          return true
        },
        cellClass: EDITABLE_SELECT_CLASS,
      },
      { field: 'joinDate', headerName: '가입일자', width: 170, cellStyle: MONO_CELL, valueFormatter: ({ value }) => formatDateTime(value) },
      { field: 'lastLogin', headerName: '마지막 접속일자', width: 170, cellStyle: MONO_CELL, valueFormatter: ({ value }) => formatDateTime(value) },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // ── Action buttons per tab ──
  const hasSelection = selectedUserIds.size > 0
  const actionButtons = useMemo(() => {
    switch (tab) {
      case 'active':
        // Spec: docs/spec/master/SPEC-master-user-mfa-reset-A.yaml#mfa-reset-button-sits-between-delete-and-password-reset
        return (
          <>
            <SecondaryButton variant="outlined" size="small" label="삭제" disabled={!hasSelection} onClick={() => setDeleteOpen(true)} />
            <SecondaryButton variant="outlined" size="small" label="MFA 초기화" disabled={!hasSelection} onClick={() => setMfaResetOpen(true)} />
            <PrimaryButton variant="filled" size="small" label="비밀번호 초기화" disabled={!hasSelection} onClick={() => setPwResetOpen(true)} />
          </>
        )
      case 'pending':
        return (
          <>
            <SecondaryButton variant="outlined" size="small" label="삭제" disabled={!hasSelection} onClick={() => setDeleteOpen(true)} />
            <PrimaryButton variant="filled" size="small" label="가입승인" disabled={!hasSelection} onClick={() => setApproveOpen(true)} />
          </>
        )
      case 'activation-request':
        return <PrimaryButton variant="filled" size="small" label="활성화 승인" disabled={!hasSelection} onClick={() => setActivationOpen(true)} />
      case 'invitation-expired':
        return (
          <PrimaryButton variant="filled" size="small" label="초대메일 재발송" disabled={!hasSelection} onClick={() => setInviteResendOpen(true)} />
        )
      default:
        return null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, hasSelection])

  // ── 마스터 권한 가드 (BE @PreAuthorize 와 이중) ──
  if (AuthStorage.user.get().auth !== 'MASTER') {
    return (
      <PageLayout gap={16}>
        <VStack gap={12}>
          <TitleSection title="유저관리" />
          <Typography name="base-text-l" colorToken="fg">
            마스터 권한이 있는 사용자만 접근할 수 있습니다.
          </Typography>
        </VStack>
      </PageLayout>
    )
  }

  // ── Render ──
  return (
    <FormProvider {...ownerForm}>
      <AgEditableCellStyles />
      <PageLayout gap={16} className="um-grid-skin">
        <VStack gap={12}>
          <TitleSection title="유저관리" />
          <FilterListV2<OwnerFilterForm>
            list={ownerFilterList}
            onSearch={handleOwnerFilterSearch}
            onReset={handleOwnerFilterReset}
            initialValues={{ shipperCodes: [], oktaUserId: undefined }}
          />
        </VStack>

        <ResizableContentsV2
          contentMinHeight={300}
          list={[
            {
              initialHeight: 300,
              content: (
                <GridTableV2<ShipperMasterRow>
                  gridKey="MA15_SHIP"
                  ref={ownerGridRef}
                  idKey="shipperCode"
                  rowData={owners}
                  columnDefs={ownerColDefs}
                  gridOptions={{ popupParent: document.body }}
                  totalCount={ownerTotalCount}
                  rowStartIndex={ownerPage * ownerPageSize}
                  rowSelection="single"
                  useColumnNo
                  showTableHeader={false}
                  onSelectionChanged={handleOwnerSelectionChanged}
                  onCellValueChanged={handleOwnerCellValueChanged}
                  onRowDoubleClicked={(e) => e.data && handleOwnerRowDoubleClicked(e.data)}
                  onFirstDataRendered={handleOwnerDataRendered}
                  noRowsOverlayComponentParams={{ iconName: 'ic-paperWarning', mainContent: '검색결과가 없습니다', subContent: '' }}
                  title={
                    <>
                      <HStack gap={8} align="center">
                        <HStack gap={4}>
                          <Typography name="base-text-m" colorToken="fg">
                            전체
                          </Typography>
                          <Typography name="base-text-m-semibold" colorToken="fg">
                            {ownerTotalCount}
                          </Typography>
                        </HStack>
                        {selectedShipperCode && (
                          <>
                            <Typography name="base-text-m" colorToken="fg-lowest">
                              ·
                            </Typography>
                            <HStack gap={4}>
                              <Typography name="base-text-m" colorToken="fg">
                                선택
                              </Typography>
                              <Typography name="base-text-l-semibold" colorToken="layerDesktop-fg-accent">
                                1
                              </Typography>
                            </HStack>
                          </>
                        )}
                        <PrimaryButton
                          variant="outlined"
                          size="small"
                          label="부서 추가"
                          disabled={!radioSelectedOwner}
                          onClick={() => {
                            setDeptName('')
                            setDeptManager('')
                            setDeptDialogOpen(true)
                          }}
                        />
                      </HStack>
                      <Pagination {...ownerPaginationProps} />
                    </>
                  }
                />
              ),
            },
            {
              initialHeight: 560,
              content: (
                <VStack style={{ height: '100%' }}>
                  {/* Tab header */}
                  <TabWrapper>
                    <Tab.Root<UserManagementTab> value={tab} onValueChange={switchTab} size="medium">
                      <Tab.Item value="active" label={`가입완료 ${tabCounts?.active ?? 0}`} />
                      <Tab.Item value="pending" label={`가입승인요청 ${tabCounts?.pending ?? 0}`} />
                      <Tab.Item value="invitation-expired" label={`초대만료 ${tabCounts?.['invitation-expired'] ?? 0}`} />
                      <Tab.Item value="activation-request" label={`활성화요청 ${tabCounts?.['activation-request'] ?? 0}`} />
                    </Tab.Root>
                  </TabWrapper>

                  {/* Action bar */}
                  <ActionBar>
                    <HStack gap={8} align="center">
                      <HStack gap={4}>
                        <Typography name="base-text-m" colorToken="fg">
                          전체
                        </Typography>
                        <Typography name="base-text-m-semibold" colorToken="fg">
                          {userTotalCount}
                        </Typography>
                      </HStack>
                      {selectedUserIds.size > 0 && (
                        <>
                          <Typography name="base-text-m" colorToken="fg-lowest">
                            ·
                          </Typography>
                          <HStack gap={4}>
                            <Typography name="base-text-m" colorToken="fg">
                              선택
                            </Typography>
                            <Typography name="base-text-l-semibold" colorToken="layerDesktop-fg-accent">
                              {selectedUserIds.size}
                            </Typography>
                          </HStack>
                        </>
                      )}
                      {actionButtons}
                    </HStack>
                    <HStack gap={12} align="center">
                      <SearchBar value={userSearch} onChange={setUserSearch} placeholder="검색" iconSize={20} />
                      <Pagination {...userPaginationProps} />
                    </HStack>
                  </ActionBar>

                  {/* User table */}
                  <UserTableWrap>
                    <GridTableV2<MasterUser>
                      gridKey="MA15_USER"
                      ref={userGridRef}
                      idKey="id"
                      rowData={tabUsers}
                      columnDefs={currentUserColDefs}
                      gridOptions={{ popupParent: document.body }}
                      totalCount={userTotalCount}
                      rowStartIndex={userPage * userPageSize}
                      rowSelectable
                      useColumnNo
                      showTableHeader={false}
                      radius="0"
                      onSelectionChanged={handleUserSelectionChanged}
                      onCellValueChanged={handleUserCellValueChanged}
                      noRowsOverlayComponentParams={{
                        iconName: 'ic-paperWarning',
                        mainContent: userGridEmptyMessage(!!userSearch, !!selectedShipperCode),
                        subContent: '',
                      }}
                    />
                  </UserTableWrap>
                </VStack>
              ),
            },
          ]}
        />
      </PageLayout>

      {/* ══ Modals ══ */}

      {/* 삭제 (가입완료 · 가입승인요청 탭) */}
      <FigmaDialog
        open={deleteOpen}
        title={`${selectedUserIds.size}개의 계정을 삭제하시겠어요?`}
        actions={
          <>
            <SecondaryButton variant="outlined" size="medium" widthMode="compact" label="취소" onClick={() => setDeleteOpen(false)} />
            <NegativeButton variant="filled" size="medium" widthMode="compact" label="삭제" onClick={confirmDelete} />
          </>
        }
      >
        <Typography name="base-text-m" colorToken="dialog-fg-description">
          삭제된 계정은 복구할 수 없습니다
        </Typography>
      </FigmaDialog>

      {/* 비밀번호 초기화 */}
      <FigmaDialog
        open={pwResetOpen}
        title={`${selectedUserIds.size}개의 계정을 비밀번호 초기화하시겠어요?`}
        actions={
          <>
            <SecondaryButton variant="outlined" size="medium" widthMode="compact" label="취소" onClick={() => setPwResetOpen(false)} />
            <PrimaryButton variant="filled" size="medium" widthMode="compact" label="비밀번호 초기화" onClick={confirmPwReset} />
          </>
        }
      >
        <VStack gap={4}>
          {selectedUserEmails.map((e) => (
            <Typography key={e} name="base-text-m" colorToken="dialog-fg-description">
              {e}
            </Typography>
          ))}
        </VStack>
      </FigmaDialog>

      <FigmaDialog
        open={mfaResetOpen}
        title={`${selectedUserIds.size}개의 계정의 MFA를 초기화하시겠어요?`}
        actions={
          <>
            <SecondaryButton variant="outlined" size="medium" widthMode="compact" label="취소" onClick={() => setMfaResetOpen(false)} />
            <PrimaryButton variant="filled" size="medium" widthMode="compact" label="MFA 초기화" onClick={confirmMfaReset} />
          </>
        }
      >
        <Typography name="base-text-m" colorToken="dialog-fg-description">
          초기화된 계정은 다음 로그인 시 인증 수단을 다시 등록해야 합니다
        </Typography>
      </FigmaDialog>

      {/* 가입승인 */}
      <FigmaDialog
        open={approveOpen}
        title={`${selectedUserIds.size}개의 계정을 가입승인하시겠어요?`}
        actions={
          <>
            <NegativeButton variant="outlined" size="medium" widthMode="compact" label="반려" onClick={confirmApproveReject} />
            <PrimaryButton variant="filled" size="medium" widthMode="compact" label="승인" onClick={confirmApprove} />
          </>
        }
      >
        <VStack gap={4}>
          {selectedUserEmails.map((e) => (
            <Typography key={e} name="base-text-m" colorToken="dialog-fg-description">
              {e}
            </Typography>
          ))}
        </VStack>
      </FigmaDialog>

      {/* 초대메일 재발송 */}
      <FigmaDialog
        open={inviteResendOpen}
        title={`${selectedUserIds.size}개의 계정에 초대메일 재발송하시겠어요?`}
        actions={
          <>
            <SecondaryButton variant="outlined" size="medium" widthMode="compact" label="취소" onClick={() => setInviteResendOpen(false)} />
            <PrimaryButton variant="filled" size="medium" widthMode="compact" label="초대메일 재발송" onClick={confirmInviteResend} />
          </>
        }
      >
        <VStack gap={4}>
          {selectedUserEmails.map((e) => (
            <Typography key={e} name="base-text-m" colorToken="dialog-fg-description">
              {e}
            </Typography>
          ))}
        </VStack>
      </FigmaDialog>

      {/* 활성화 승인 */}
      <FigmaDialog
        open={activationOpen}
        title={`${selectedUserIds.size}개의 계정에 활성화 승인하시겠어요?`}
        actions={
          <>
            <SecondaryButton variant="outlined" size="medium" widthMode="compact" label="취소" onClick={() => setActivationOpen(false)} />
            <PrimaryButton variant="filled" size="medium" widthMode="compact" label="활성화 승인" onClick={confirmActivation} />
          </>
        }
      >
        <VStack gap={4}>
          {selectedUserEmails.map((e) => (
            <Typography key={e} name="base-text-m" colorToken="dialog-fg-description">
              {e}
            </Typography>
          ))}
        </VStack>
      </FigmaDialog>

      {/* 멀티화주그룹 */}
      <Modal
        open={!!multiGroupOwner}
        customWidth={560}
        hideFooter
        title={`멀티화주그룹 — ${owners.find((o) => o.shipperCode === multiGroupOwner)?.shipperName ?? ''}`}
        onClose={closeMultiGroup}
      >
        <DialogBody>
          <VStack gap={16}>
            <GroupTagWrap>
              {(ownerGroupsMap.get(multiGroupOwner ?? '') ?? []).length === 0 && (
                <Typography name="base-text-l" colorToken="fg-lower">
                  그룹이 없습니다.
                </Typography>
              )}
              {(ownerGroupsMap.get(multiGroupOwner ?? '') ?? []).map((g) => (
                <GroupTag key={g}>
                  <Typography name="base-text-s" colorToken="fg">
                    {g}
                  </Typography>
                  <GroupTagRemoveBtn type="button" onClick={() => removeFromGroup(multiGroupOwner, g)}>
                    <Icon name="ic-closeCircle" size={14} filled colorToken="fg-lower" />
                  </GroupTagRemoveBtn>
                </GroupTag>
              ))}
            </GroupTagWrap>
            <GroupDivider />
            <HStack gap={8} align="center">
              <Textfield.Box style={{ flex: 1 }}>
                <Textfield.Input
                  value={newGroupInput}
                  onValueChange={setNewGroupInput}
                  placeholder="추가할 그룹명 입력 후 Enter"
                  onKeyDown={(e: React.KeyboardEvent) => {
                    // 한글 IME 조합 확정 Enter 가 중복 발동해 그룹이 두 개로 쪼개져 추가되는 것 방지
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) addToGroup(multiGroupOwner)
                  }}
                />
              </Textfield.Box>
              <PrimaryButton
                variant="outlined"
                size="large"
                label="추가"
                disabled={!newGroupInput.trim()}
                onClick={() => addToGroup(multiGroupOwner)}
              />
            </HStack>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <SecondaryButton variant="outlined" size="medium" widthMode="compact" label="취소" onClick={closeMultiGroup} />
          <PrimaryButton variant="filled" size="medium" widthMode="compact" label="저장" onClick={saveMultiGroup} />
        </DialogFooter>
      </Modal>

      {/* 부서 추가 */}
      <Modal open={deptDialogOpen} customWidth={480} hideFooter title="부서 추가" onClose={() => setDeptDialogOpen(false)}>
        <DialogBody>
          <FormSection>
            <FieldGroup>
              <FieldLabelRow>
                <Typography name="base-text-l-medium" colorToken="fg">
                  화주명
                </Typography>
              </FieldLabelRow>
              <DisabledInput>
                <Typography name="base-text-l" colorToken="fg">
                  {radioSelectedOwner?.shipperName ?? ''}
                </Typography>
              </DisabledInput>
            </FieldGroup>
            <FieldGroup>
              <FieldLabelRow>
                <Typography name="base-text-l-medium" colorToken="fg">
                  부서명
                </Typography>
                <Typography name="base-text-l-medium" colorToken="layerDesktop-fg-accent">
                  (필수)
                </Typography>
              </FieldLabelRow>
              <Textfield.Box>
                <Textfield.Input value={deptName} onValueChange={setDeptName} placeholder="부서명 입력" />
                {deptName && (
                  <Textfield.Slot>
                    <Icon
                      name="ic-closeCircle"
                      size={16}
                      filled
                      colorToken="fg-lower"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setDeptName('')}
                    />
                  </Textfield.Slot>
                )}
              </Textfield.Box>
            </FieldGroup>
            <FieldGroup>
              <FieldLabelRow>
                <Typography name="base-text-l-medium" colorToken="fg">
                  책임자명
                </Typography>
              </FieldLabelRow>
              <Textfield.Box>
                <Textfield.Input value={deptManager} onValueChange={setDeptManager} placeholder="책임자명 입력" />
                {deptManager && (
                  <Textfield.Slot>
                    <Icon
                      name="ic-closeCircle"
                      size={16}
                      filled
                      colorToken="fg-lower"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setDeptManager('')}
                    />
                  </Textfield.Slot>
                )}
              </Textfield.Box>
            </FieldGroup>
          </FormSection>
        </DialogBody>
        <DialogFooter>
          <SecondaryButton variant="outlined" size="medium" widthMode="compact" label="취소" onClick={() => setDeptDialogOpen(false)} />
          <PrimaryButton variant="filled" size="medium" widthMode="compact" label="추가" disabled={!deptName.trim()} onClick={handleAddDept} />
        </DialogFooter>
      </Modal>

      {/* SnackBar */}
      <SnackBarListBox list={snackList} onClose={closeSnack} />
    </FormProvider>
  )
}

export default UserManagement
